import React, { useCallback } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import PublishIcon from '@material-ui/icons/Publish';
import { IntlMsg } from '../_core/IntlMsg/intl-msg.component';
import { ZoomableImage } from '../_core/_ui/zoomable-image.component';
import { messages } from './format-template.messages';

// The backend only mints uploads for these types; anything else is refused at issue time.
const DIAGRAM_ACCEPT = 'image/png,image/jpeg';

interface IDiagramUploadRowProps {
  label?: React.ReactNode;
  alt: string;
  imageUrl?: string;
  // Object URL of a selected replacement awaiting the template save.
  pendingPreviewUrl?: string;
  // Caption shown while a replacement is pending; create-time callers word it differently.
  pendingMessage?: { id: string; defaultMessage: string };
  uploading: boolean;
  disabled: boolean;
  onSelectFile: (file: File) => void;
}

// One diagram slot — current image, pending replacement, and the replace affordance — shared by
// the catalog editor's per-band rows and the custom edit modal's document diagram.
export const DiagramUploadRow: React.FC<IDiagramUploadRowProps> = ({
  label,
  alt,
  imageUrl,
  pendingPreviewUrl,
  pendingMessage = messages.diagramPending,
  uploading,
  disabled,
  onSelectFile,
}) => {
  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Clearing the value lets the same file be picked again after a failed upload.
    event.target.value = '';
    if (file) onSelectFile(file);
  }, [onSelectFile]);

  const shownUrl = pendingPreviewUrl || imageUrl;
  // Not memoized: a cheap descriptor pick over two locals, not fed to any hook dep array.
  const buttonMessage = uploading ? messages.uploadingDiagram
    : (shownUrl ? messages.replaceDiagram : messages.addDiagram);

  return (
    <Box mt={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="body2">{label}</Typography>
        <Button
          size="small"
          component="label"
          startIcon={uploading ? <CircularProgress size={14} /> : <PublishIcon />}
          disabled={disabled || uploading}
        >
          <IntlMsg {...buttonMessage} />
          <input type="file" accept={DIAGRAM_ACCEPT} hidden onChange={handleFileChange} />
        </Button>
      </Box>
      {pendingPreviewUrl && (
        <Typography variant="caption" color="textSecondary">
          <IntlMsg {...pendingMessage} />
        </Typography>
      )}
      {shownUrl ? (
        <Box mt={1}>
          <ZoomableImage src={shownUrl} alt={alt} minHeight={100} maxHeight={180} mb={0} />
        </Box>
      ) : (
        <Typography variant="caption" color="textSecondary" display="block">
          <IntlMsg {...messages.noDiagram} />
        </Typography>
      )}
    </Box>
  );
};
